import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calculator, 
  Receipt, 
  PiggyBank, 
  CreditCard, 
  BarChart3, 
  LogOut, 
  User,
  Menu,
  X,
  Building,
  Wallet,
  Bell,
  DollarSign,
  TrendingUp,
  FileText,
  HelpCircle,
  Info,
  MoreVertical,
  ChevronDown,
  Shield,
  Scale
} from 'lucide-react';
import BillsDueNotification from './BillsDueNotification';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isStockMarketMenuOpen, setIsStockMarketMenuOpen] = useState(false);
  const [isFinancesMenuOpen, setIsFinancesMenuOpen] = useState(false);
  const [isTaxesMenuOpen, setIsTaxesMenuOpen] = useState(false);
  const [showBillsNotification, setShowBillsNotification] = useState(false);

  // Main navigation - core features only (clear, action-oriented names)
  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    { name: 'Finances', href: null, icon: DollarSign, isDropdown: true }, // Dropdown menu
    { name: 'Taxes', href: null, icon: Receipt, isDropdown: true }, // Dropdown menu
    { name: 'Stock Market', href: null, icon: TrendingUp, isDropdown: true }, // Dropdown menu
  ];

  // Finances dropdown items
  const financesMenu = [
    { name: 'Income', href: '/income-tracker', icon: DollarSign },
    { name: 'Expenses', href: '/expense-tracker', icon: Receipt },
    { name: 'Monthly Bills', href: '/monthly-bills', icon: Receipt },
    { name: 'Bank Accounts', href: '/account-manager', icon: Wallet },
    { name: 'Investment Accounts', href: '/taxable-accounts', icon: CreditCard },
    { name: 'Real Estate', href: '/property-manager', icon: Building },
    { name: 'Mortgage Calculator', href: '/mortgage-calculator', icon: Calculator },
  ];

  // Taxes dropdown items
  const taxesMenu = [
    { name: 'Tax Forms', href: '/tax-forms', icon: FileText },
    { name: 'Property Questionnaire', href: '/property-tax-questionnaire', icon: Building },
    { name: 'Tax Planning', href: '/expense-tracker', icon: Receipt },
    { name: 'Roth IRA', href: '/roth-ira-planner', icon: PiggyBank },
  ];

  // Stock Market dropdown items (hide Bulk for demo users)
  const stockMarketMenu = [
    { name: 'Individual Analysis', href: '/individual-stock-analysis', icon: TrendingUp },
    ...(user?.isDemo ? [] : [{ name: 'Bulk Analysis', href: '/bulk-stock-analysis', icon: BarChart3 }]),
  ];

  // Secondary navigation - grouped in "More" menu (organized by category)
  const moreNavigation = [
    // Stock Analysis
    { name: 'Auto Trading', href: '/automated-trader', icon: TrendingUp, category: 'Stock Analysis' },
  ];

  // Resources menu - About, Help, Platform Overview, Examples, etc.
  const resourcesMenu = [
    { name: 'Platform Overview', href: '/platform', icon: Info },
    { name: 'Page Examples', href: '/examples', icon: Info },
    { name: 'About', href: '/about', icon: Info },
    { name: 'Help & Support', href: '/help', icon: HelpCircle },
    { name: 'Legal', href: '/legal', icon: Scale },
    { name: 'Terms of Service', href: '/terms', icon: Info },
    { name: 'Privacy Policy', href: '/privacy', icon: Info },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  // Don't show navbar on landing page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">WealthCore</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {mainNavigation.map((item) => {
              const Icon = item.icon;
              
              // Handle dropdown menus
              if (item.isDropdown) {
                let menuItems = [];
                let isMenuOpen = false;
                let setIsMenuOpen = null;
                
                if (item.name === 'Finances') {
                  menuItems = financesMenu;
                  isMenuOpen = isFinancesMenuOpen;
                  setIsMenuOpen = setIsFinancesMenuOpen;
                } else if (item.name === 'Taxes') {
                  menuItems = taxesMenu;
                  isMenuOpen = isTaxesMenuOpen;
                  setIsMenuOpen = setIsTaxesMenuOpen;
                } else if (item.name === 'Stock Market') {
                  menuItems = stockMarketMenu;
                  isMenuOpen = isStockMarketMenuOpen;
                  setIsMenuOpen = setIsStockMarketMenuOpen;
                }
                
                return (
                  <div key={item.name} className="relative">
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                        isMenuOpen || menuItems.some(subItem => isActive(subItem.href))
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isMenuOpen && (
                      <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                        {menuItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => setIsMenuOpen(false)}
                              className={`block px-4 py-2 text-sm transition-colors duration-200 flex items-center space-x-2 ${
                                isActive(subItem.href)
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular link items
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* More Menu Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                  isMoreMenuOpen || moreNavigation.some(item => isActive(item.href)) || resourcesMenu.some(item => isActive(item.href))
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MoreVertical className="w-4 h-4" />
                <span>More</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* More Dropdown Menu */}
              {isMoreMenuOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {/* Stock Analysis Section - only show if there are items */}
                  {moreNavigation.filter(item => item.category === 'Stock Analysis').length > 0 && (
                    <>
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                        Stock Analysis
                      </div>
                      {moreNavigation.filter(item => item.category === 'Stock Analysis').map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsMoreMenuOpen(false)}
                            className={`block px-4 py-2 text-sm transition-colors duration-200 flex items-center space-x-2 ${
                              isActive(item.href)
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </>
                  )}
                  
                  {/* Resources Section */}
                  {moreNavigation.filter(item => item.category === 'Stock Analysis').length > 0 && (
                    <div className="border-t border-gray-100 my-1"></div>
                  )}
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Resources
                  </div>
                  {resourcesMenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsMoreMenuOpen(false)}
                        className={`block px-4 py-2 text-sm transition-colors duration-200 flex items-center space-x-2 ${
                          isActive(item.href)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Bills Notification Button */}
            <button
              onClick={() => setShowBillsNotification(true)}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
              title="Bills Due This Month"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            </button>
            
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="hidden sm:block text-gray-700 font-medium">
                  {user?.email || 'User'}
                </span>
              </button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                    <p className="font-medium">{user?.email}</p>
                    <p className="text-gray-500 text-xs">WealthCore User</p>
                  </div>
                  {/* Admin Panel Link - Only show for admin users */}
                  {(user?.email === 'tnputnam@live.com' || user?.role === 'admin') && (
                    <Link
                      to="/admin"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Main Navigation */}
            {mainNavigation.map((item) => {
              const Icon = item.icon;
              
              // Handle dropdown menu for Stock Market in mobile
              if (item.isDropdown && item.name === 'Stock Market') {
                return (
                  <div key={item.name}>
                    <div className="px-3 py-2 text-base font-medium text-gray-600 flex items-center space-x-3">
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </div>
                    <div className="pl-8 space-y-1">
                      {stockMarketMenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-3 ${
                              isActive(subItem.href)
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            <SubIcon className="w-4 h-4" />
                            <span>{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              
              // Regular link items
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center space-x-3 ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* Stock Analysis Section - only show if there are items */}
            {moreNavigation.filter(item => item.category === 'Stock Analysis').length > 0 && (
              <div className="border-t border-gray-200 my-2 pt-2">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Stock Analysis
                </div>
                {moreNavigation.filter(item => item.category === 'Stock Analysis').map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center space-x-3 ${
                        isActive(item.href)
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Resources Section */}
            <div className="border-t border-gray-200 my-2 pt-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Resources
              </div>
              {resourcesMenu.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center space-x-3 ${
                      isActive(item.href)
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Bills Due Notification Modal */}
      {showBillsNotification && (
        <BillsDueNotification onClose={() => setShowBillsNotification(false)} />
      )}

      {/* Click outside to close dropdowns */}
      {(isMoreMenuOpen || isUserMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsMoreMenuOpen(false);
            setIsUserMenuOpen(false);
          }}
        />
      )}
    </nav>
  );
};

export default Navbar;
